import { FC, useState } from 'react';
import { camelCase } from 'lodash';
import { gql, useApolloClient } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import Button from 'jbc-front/components/Button';
import { useNotify } from '@/hooks/useNotify';
import { logError } from '@/libs/telemetry';
import type { Schema } from '../../schema';

const REGULAR_HEALTH_CHECKUP_RESULT_AUTO_SUMMARY = gql`
  query regularHealthCheckupResultAutoSummary(
    $officeId: ID
    $aggregationOfficeIds: [ID!]
    $startOn: Date
    $endOn: Date
  ) {
    client {
      regularHealthCheckupReportAutoSummary(
        officeId: $officeId
        aggregationOfficeIds: $aggregationOfficeIds
        startOn: $startOn
        endOn: $endOn
      ) {
        resultSummaries {
          itemCode
          totalCount
          hasFindingCount
        }
        specifiedWorkSummaries {
          specifiedWorkCode
          count
        }
        enrolledEmployeesCount
        checkedEmployeesCount
      }
    }
  }
`;

interface RegularHealthCheckupResultAutoSummaryQueryVariables {
  officeId?: string;
  aggregationOfficeIds?: string[];
  startOn?: string;
  endOn?: string;
}

interface RegularHealthCheckupResultAutoSummaryQueryResult {
  client: {
    regularHealthCheckupReportAutoSummary: {
      resultSummaries: {
        itemCode: string;
        totalCount: number;
        hasFindingCount: number;
      }[];
      specifiedWorkSummaries: {
        specifiedWorkCode: string;
        count: number;
      }[];
      enrolledEmployeesCount: number | null;
      checkedEmployeesCount: number;
    };
  };
}

interface RefreshSummaryProps {
  className?: string;
  disabled?: boolean;
}

export const RefreshSummary: FC<RefreshSummaryProps> = ({ className, disabled }) => {
  const apolloClient = useApolloClient();
  const notify = useNotify();
  const { getValues, setValue } = useFormContext<Schema>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleClick = async () => {
    setIsRefreshing(true);
    try {
      const result = await apolloClient.query<
        RegularHealthCheckupResultAutoSummaryQueryResult,
        RegularHealthCheckupResultAutoSummaryQueryVariables
      >({
        query: REGULAR_HEALTH_CHECKUP_RESULT_AUTO_SUMMARY,
        variables: {
          officeId: getValues('officeId'),
          aggregationOfficeIds: getValues('aggregationOfficeIds'),
          startOn: getValues('startOn'),
          endOn: getValues('endOn')
        },
        fetchPolicy: 'no-cache'
      });
      const { resultSummaries, specifiedWorkSummaries, enrolledEmployeesCount, checkedEmployeesCount } =
        result.data.client.regularHealthCheckupReportAutoSummary;
      for (const resultSummary of resultSummaries) {
        const itemCode = camelCase(resultSummary.itemCode) as keyof Schema['resultSummaries'];
        setValue(`resultSummaries.${itemCode}.totalCount`, `${resultSummary.totalCount}`);
        setValue(`resultSummaries.${itemCode}.hasFindingCount`, `${resultSummary.hasFindingCount}`);
      }
      for (const specifiedWorkSummary of specifiedWorkSummaries) {
        const specifiedWorkCode = camelCase(specifiedWorkSummary.specifiedWorkCode);
        setValue(`specifiedWorkSummaries.${specifiedWorkCode}.count`, `${specifiedWorkSummary.count}`);
      }
      if (enrolledEmployeesCount !== null) {
        setValue('enrolledEmployeesCount', `${enrolledEmployeesCount}`);
      }
      setValue('checkedEmployeesCount', `${checkedEmployeesCount}`);
      notify('再集計しました', 'success');
    } catch (err) {
      if (err instanceof Error) {
        logError(err);
      }
      notify('予期せぬエラーが発生しました', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Button className={className} disabled={disabled || isRefreshing} onClick={handleClick}>
      再集計
    </Button>
  );
};
